export default function PortStLucieDestinationsSection() {
  const stats = [
    {
      value: "42%",
      label: "of Port St. Lucie requests are golf or cruise-related travel with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for Caribbean cruise and golf trip bookings",
    },
    {
      value: "Dec–Apr",
      label: "peak golf and snowbird season — passport renewals spike before international trips",
    },
    {
      value: "48-hour",
      label: "shortest cruise departure window we've accommodated for a Port St. Lucie family",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Scotland & Ireland",
      context: "The premier golf destinations for Port St. Lucie's PGA Village community. St. Andrews, Carnoustie, Royal County Down, and Ballybunion draw organized group trips and individual golfers throughout the spring and fall seasons. These trips are often booked 3–6 months in advance through PGA Village travel programs, but individual golfers may book with as little as 2–4 weeks' notice when tee time openings or group trip cancellations create last-minute opportunities.",
      urgency: "Organized group trips have set departure dates — a passport issue discovered after deposits are paid can mean losing your spot and your money",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Port St. Lucie families — whether by cruise from Port Canaveral or Fort Lauderdale, or by connecting flight from PBI or MCO to Nassau, Montego Bay, or San Juan. Families from Port St. Lucie's suburban communities regularly sail to the Bahamas, Jamaica, the Cayman Islands, and the Eastern Caribbean. All cruise passengers and most air travelers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Canada is a significant golf and leisure destination for Port St. Lucie retirees, with Banff, Whistler, and Ontario's Muskoka region drawing seasonal golf trips. Niagara Falls and Toronto attract family vacationers. Healthcare professionals also travel to Canadian medical conferences. Canada requires a valid passport for air travel, and while land border crossings from the U.S. may accept enhanced driver's licenses, international flights to Canada do not.",
      urgency: "Golf trips to Canada are often booked in organized group packages with non-refundable deposits — passport issues after booking are costly",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & London",
      context: "London draws Port St. Lucie travelers for medical conferences, golf trips to England's historic courses, and family vacations. The Open Championship at courses like Royal St. George's, Royal Liverpool, and Royal Birkdale attracts golf enthusiasts from PGA Village. Healthcare professionals from Cleveland Clinic Tradition and St. Lucie Medical Center attend conferences in London, Edinburgh, and Dublin.",
      urgency: "Conference registration deadlines and golf trip deposits create fixed travel dates — passport issues discovered after booking require expedited service",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Latin America",
      context: "Cancun, Riviera Maya, and Los Cabos are staple beach destinations for Port St. Lucie families seeking warm-weather vacations within a short flight. PBI and MCO offer connecting service through Atlanta, Charlotte, and Houston. Business travelers also fly to Mexico City for corporate meetings. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for Port St. Lucie families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Europe — France, Italy & Spain",
      context: "European cultural destinations draw Port St. Lucie families during summer breaks and retirees during shoulder seasons. Paris, Rome, Barcelona, and the Amalfi Coast are popular for multi-generational trips. College students from Indian River State College and Florida Atlantic University Treasure Coast campuses participate in study abroad programs in Spain, Italy, and France. These programs often have application deadlines that require current passports months in advance.",
      urgency: "Study abroad programs require passport submission at application time — not at departure — creating a 4–6 month advance need",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Port St. Lucie Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Port St. Lucie
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Port St. Lucie travelers head to Scotland and Ireland for golf, the Caribbean for family cruises, and Europe for conferences and cultural trips. These are the destinations that create the most passport urgency for our Port St. Lucie clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout the Treasure Coast</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Stuart", href: "/stuart" },
              { name: "Jensen Beach", href: "/jensen-beach" },
              { name: "Fort Pierce", href: "/fort-pierce" },
              { name: "Palm City", href: "/palm-city" },
              { name: "Vero Beach", href: "/vero-beach" },
              { name: "West Palm Beach", href: "/west-palm-beach" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}